// Trial helpers — Build A
//
// applyTrialIfCampaignPending: read the persisted `campaign` slug from
// sessionStorage and, if present + valid, atomically apply a 30-day Pro trial
// to the user via the apply_campaign_trial() RPC. Idempotent — the RPC's own
// guards (existing trial, paid sub, lifetime_pro) prevent double-application.
//
// Wired by the founder from auth-store.signUp() and auth-store.initialize().

import { supabase } from './supabase';
import { getPersistedUTM, persistUTM } from './utm';
import type { User } from '../types';
import { localDateString } from './dates';

/**
 * Check the persisted UTM bag for a `campaign` slug, and if present, attempt
 * to apply a campaign trial to the given user. Safe to call multiple times.
 *
 * Also calls the confirm-campaign-email Edge Function so the new account skips
 * email-verification gating for campaign-driven cold outreach signups.
 *
 * Always clears the campaign key from sessionStorage after attempt to prevent
 * a second account on the same browser from picking up the slug.
 */
export async function applyTrialIfCampaignPending(userId: string): Promise<{
  applied: boolean;
  reason?: string;
}> {
  if (!userId) return { applied: false, reason: 'missing_user_id' };

  const utm = getPersistedUTM();
  const slug = (utm as { campaign?: string }).campaign;

  if (!slug) {
    return { applied: false, reason: 'no_campaign' };
  }

  try {
    // Apply the trial via the security-definer RPC — atomic.
    const { data, error } = await supabase.rpc('apply_campaign_trial', {
      p_user_id: userId,
      p_slug: slug,
    });

    // Best-effort skip-email-verification for campaign signups.
    // Failure here is non-fatal: the user can still confirm the normal way.
    try {
      await supabase.functions.invoke('confirm-campaign-email', {
        body: { user_id: userId, slug },
      });
    } catch (err) {
      console.warn('confirm-campaign-email invoke failed:', err);
    }

    // Clear from sessionStorage either way — we tried.
    const { campaign: _drop, ...rest } = utm as { campaign?: string } & Record<string, unknown>;
    persistUTM(rest);

    if (error) {
      console.warn('apply_campaign_trial RPC error:', error.message);
      return { applied: false, reason: error.message };
    }

    const result = data as { success?: boolean; error?: string } | null;
    if (!result?.success) {
      return { applied: false, reason: result?.error || 'unknown' };
    }

    return { applied: true };
  } catch (err) {
    console.error('applyTrialIfCampaignPending failed:', err);
    return { applied: false, reason: err instanceof Error ? err.message : 'error' };
  }
}

/** True when the user has a current, unconverted trial. */
export function isOnActiveTrial(user: Pick<User, 'trial_ends_at' | 'trial_converted'> | null | undefined): boolean {
  if (!user) return false;
  if (user.trial_converted) return false;
  if (!user.trial_ends_at) return false;
  return new Date(user.trial_ends_at).getTime() > Date.now();
}

/** Days remaining on an active trial. Returns 0 if not on trial. */
export function trialDaysRemaining(
  user: Pick<User, 'trial_ends_at' | 'trial_converted'> | null | undefined
): number {
  if (!isOnActiveTrial(user)) return 0;
  const ms = new Date(user!.trial_ends_at!).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

/** Campaign tag for the account-wide complimentary Pro grant (migration 75). */
export const COMPLIMENTARY_PRO_CAMPAIGN = 'complimentary-pro';

/**
 * End date of an active complimentary Pro grant, formatted for display
 * ("June 30, 2027"), or null when the user isn't on one. Shown as a gift with
 * an end date, not as a trial countdown.
 */
export function complimentaryProUntil(
  user: Pick<User, 'trial_ends_at' | 'trial_converted' | 'trial_campaign'> | null | undefined
): string | null {
  if (!user || user.trial_campaign !== COMPLIMENTARY_PRO_CAMPAIGN || !isOnActiveTrial(user)) return null;
  return new Date(user.trial_ends_at!).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/Los_Angeles',
  });
}

/**
 * Streak as the learner should see it. users.streak_days is only updated when an answer
 * is submitted, so after someone stops practicing it keeps the old number forever. A
 * streak is alive only if the last practice day was today or yesterday on the learner's
 * calendar (the server's since migration 81); otherwise it has ended and reads 0.
 */
export function effectiveStreak(
  user: Pick<User, 'streak_days' | 'streak_last_date'> | null | undefined,
  now: Date = new Date()
): number {
  if (!user?.streak_days || !user.streak_last_date) return 0;
  const today = localDateString(now);
  const yesterday = localDateString(new Date(now.getTime() - 86400000));
  const last = user.streak_last_date.slice(0, 10);
  return last === today || last === yesterday ? user.streak_days : 0;
}
